// Angular
import { Inject, Injectable } from '@angular/core';

// Third party
import { Capacitor } from '@capacitor/core';
import { CookieService } from 'ngx-cookie-service';
import moment from 'moment';
import { DurationInputArg1, DurationInputArg2 } from 'moment';

// Local library
import { ILoggedInUserModel, Web, ipPattern } from '@amwager/util-ts';

// Local file
import { DEFAULT_ROLE_ID, DOMAIN, UI } from '../tokens';



@Injectable()
export class SessionService {

  private readonly windowHostname =  window.location.hostname;
  private readonly localStorage =  window.localStorage;
  private readonly platform = Capacitor.getPlatform();

  constructor(
    @Inject(DEFAULT_ROLE_ID) private readonly defaultRoleId: string,
    @Inject(UI) private readonly appUi: string,
    @Inject(DOMAIN) private readonly domain: string,
    private readonly cookieService: CookieService,
  ) {}

  getExpiresDate(days: number = 1, interval: string = 'days'): Date {
    const cookieMoment: moment.Moment = moment(4, "HH");

    return new Date(cookieMoment.add(
      days as DurationInputArg1,
      interval as DurationInputArg2
    ).toDate());
  }

  getOptions(path: string = '/', expDate?: number, expInterval?: string): Web.ICookie {
    return {
      path,
      domain: this.getCookieDomain(),
      expires: expDate ? this.getExpiresDate(expDate, expInterval) : expDate
    }
  }

  /**
   * Cookies
   */
  storeCookiesLoginResponse(
      data: Partial<ILoggedInUserModel>, email: string
    ): void {

    this.resetLogout();

    const options1day: Web.ICookie = this.getOptions('/', 1);
    const options7days: Web.ICookie = this.getOptions('/', 7);
    const options20yrs: Web.ICookie = this.getOptions('/', 20, 'y');

    // options 1 day
    if (data.Token) this.setUserToken(data.Token, options1day);
    if (data.TPToken) this.setTPToken(data.TPToken, options1day);
    if (data.FirstName) this.setUserFName(data.FirstName, options1day);
    if (data.LastName) this.setUserLast(data.LastName, options1day);
    if (data.DOB) this.setUserDOB(data.DOB, options1day);
    if (data.Subscription) this.setSubscription(data.Subscription, options1day);
    if (data.ReferalId) this.setReferralId(data.ReferalId, options1day);
    if (data.PaypageId) this.setPayPageId(data.PaypageId, options1day);
    if (data.Affiliate?.AffiliateName) this.setAffiliateName(
      data.Affiliate.AffiliateName,
      options1day
    );
    this.setRoleId(data.RoleId || this.defaultRoleId, options1day);

    if (data.State) this.setState(data.State, options1day);
    if (data.Country) this.setCountry(data.Country, options1day);

    // options 7 days
    if (email) this.setUserEmail(email, options7days);
    // if (password) this.setUserPassword(password, options7days);

    // options 20 years
    if (data.LastLoginTime) this.setLastLogin(data.LastLoginTime, options20yrs);
    if (this.appUi) this.setLastUI(this.appUi, options20yrs);
  }

  getCookieDomain(): string {
    const ipValidator = ipPattern;
    let cookieDomain: string = this.domain;

    if (ipValidator.test(this.windowHostname)) cookieDomain = this.windowHostname;
    else {
      if (this.windowHostname.split(".").length - 1 > 1) cookieDomain = this.domain;
      else cookieDomain = this.windowHostname;
    }

    return cookieDomain || '';
  }

  /**
   * Session
   */
  generateSessionId(): string {
    const nValues = [8,9,'a','b'];

    //  M = 1-5, N = [8,9,'a','b'],  derived from uuidv4
    const sessionId = 'xxxxxxxx-xxxx-Mxxx-Nxxx-xxxxxxxxxxxx'.replace(
      /[xyNM]/g,
      (c: string): string => {
        if ( c == 'M' ) return String(
          (Math.floor(Math.random() * 5) + 1).toString()
        );
        else if ( c == 'N' ) return String(
          nValues[ Math.floor(Math.random() * 4) ]
        );

        const r = Math.random()*16|0, v = c === 'x' ? r : (r&0x3|0x8);
        return v.toString(16);
    });

    // if (isProfiling) this.commonFacade.setTmsId(sessionId);

    return sessionId;
  }

  /**
   * Reset logout
   */
  resetLogout(): void {
    const options7days = this.getOptions('/', 7);
    const options20yrs: Web.ICookie = this.getOptions('/', 20, 'y');

    const isRememberMe: boolean = this.getIsRememberMe();
    const lastlogin: string = this.getLastLogin();

    let tempEmail = '';

    // get email before deleting all cookies
    if (isRememberMe) tempEmail = this.getIsRememberEmail();

    this.cookieService.deleteAll('/', options7days.domain);
    this.localStorage.clear();

    if (isRememberMe && tempEmail) {
      this.setUserEmail(tempEmail, options7days);

      if (tempEmail !== 'Guest')
        this.setIsRememberEmail(tempEmail, options20yrs);

      this.setIsRememberMe(String(isRememberMe), options20yrs);
    }

    this.setLastLogin(lastlogin, options20yrs);
    this.setLastUI(this.appUi, options20yrs);
  }

  /**
   * Token
   */
  hasToken(): boolean {
    const token = this.getUserToken();
    return (token !== null) && (token !== 'null') && (token !== undefined)
      && (token !== 'undefined') && (token !== '');
  }

  getUserToken(): string {
    return this.cookieService.get("userToken") || '';
  }

  setUserToken(token: string, options: Web.ICookie): void {
    this.delUserToken(options);

    this.cookieService.set(
      "userToken",
      token,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delUserToken(options: Web.ICookie): void {
    this.cookieService.delete("userToken", options.path, options.domain);
  }

  /**
   * TP Token
   */
  getTPToken(): string {
    return this.cookieService.get('TpToken') || '';
  }

  setTPToken(tptoken: string, options: Web.ICookie): void {
    this.delTPToken(options);

    this.cookieService.set(
      "TpToken",
      tptoken,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delTPToken(options: Web.ICookie): void {
    this.cookieService.delete("TpToken", options.path, options.domain);
  }

  /**
   * User first name
   */
  getUserFName(): string {
    return this.cookieService.get("userFirst") || '';
  }

  setUserFName(fname: string, options: Web.ICookie): void {
    this.delUserFName(options);

    this.cookieService.set(
      "userFirst",
      fname,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delUserFName(options: Web.ICookie): void {
    this.cookieService.delete("userFirst", options.path, options.domain);
  }

  /**
   * User last name
   */
  getUserLast(): string {
    return this.cookieService.get("userLast") || '';
  }

  setUserLast(lastName: string, options: Web.ICookie): void {
    this.delUserLast(options);

    this.cookieService.set(
      "userLast",
      lastName,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delUserLast(options: Web.ICookie): void {
    this.cookieService.delete("userLast", options.path, options.domain);
  }

  /**
   * User Date of Birth
   */
  getUserDOB(): string {
    return this.cookieService.get("userDOB") || '';
  }

  setUserDOB(userDOB: string, options: Web.ICookie): void {
    this.delUserDOB(options);

    this.cookieService.set(
      "userDOB",
      userDOB,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delUserDOB(options: Web.ICookie): void {
    this.cookieService.delete("userDOB", options.path, options.domain);
  }

  /**
   * User Email
   */
  getUserEmail(): string {
    return this.cookieService.get("userEmail") || '';
  }

  setUserEmail(email: string, options: Web.ICookie): void {
    this.delUserEmail(options);

    this.cookieService.set(
      "userEmail",
      email,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delUserEmail(options: Web.ICookie): void {
    this.cookieService.delete("userEmail", options.path, options.domain);
  }

  /**
   * User Password
   */
  // getUserPassword(): string {
  //   return this.cookieService.get("userPassword") || '';
  // }

  // setUserPassword(email: string, options: Web.ICookie): void {
  //   this.delUserPassword(options);

  //   this.cookieService.set(
  //     "userPassword",
  //     email,
  //     options.expires,
  //     options.path,
  //     options.domain
  //   );
  // }

  // delUserPassword(options: Web.ICookie): void {
  //   this.cookieService.delete("userPassword", options.path, options.domain);
  // }

  /**
   * User Address
   */
  // getUserAddress(): string {
  //   return this.cookieService.get("userAddress") || '';
  // }

  // setUserAddress(address: string, options: Web.ICookie): void {
  //   this.delUserAddress(options);

  //   this.cookieService.set(
  //     "userAddress",
  //     address,
  //     options.expires,
  //     options.path,
  //     options.domain
  //   );
  // }

  // delUserAddress(options: Web.ICookie): void {
  //   this.cookieService.delete("userAddress", options.path, options.domain);
  // }

  /**
   * User Location
   */
  // getUserLocation(): string {
  //   return this.cookieService.get("userLocation") || '';
  // }

  // setUserLocation(location: string, options: Web.ICookie): void {
  //   this.delUserLocation(options);

  //   this.cookieService.set(
  //     "userLocation",
  //     location,
  //     options.expires,
  //     options.path,
  //     options.domain
  //   );
  // }

  // delUserLocation(options: Web.ICookie): void {
  //   this.cookieService.delete("userLocation", options.path, options.domain);
  // }

  /**
   * Subscription
   */
  getSubscription(): string {
    return this.cookieService.get("Subscription") || '';
  }

  setSubscription(subscription: string, options: Web.ICookie): void {
    this.delSubscription(options);

    this.cookieService.set(
      "Subscription",
      subscription,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delSubscription(options: Web.ICookie): void {
    this.cookieService.delete("Subscription", options.path, options.domain);
  }

  /**
   * AffiliateName
   */
  getAffiliateName(): string {
    return this.cookieService.get("AffiliateName") || '';
  }

  setAffiliateName(affiliateName: string, options: Web.ICookie): void {
    this.delAffiliateName(options);

    this.cookieService.set(
      "AffiliateName",
      affiliateName,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delAffiliateName(options: Web.ICookie): void {
    this.cookieService.delete("AffiliateName", options.path, options.domain);
  }

  /**
   * Last login
   */
  getLastLogin(): string {
    return this.cookieService.get("lastLogin") || '';
  }

  setLastLogin(lastLogin: string, options: Web.ICookie): void {
    this.delLastLogin(options);

    this.cookieService.set(
      "lastLogin",
      lastLogin,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delLastLogin(options: Web.ICookie): void {
    this.cookieService.delete("lastLogin", options.path, options.domain);
  }

  /**
   * Referral Id
   */
  getReferralId(): string {
    return this.cookieService.get("ReferralId") || '';
  }

  setReferralId(referralId: string, options: Web.ICookie): void {
    this.delReferralId(options);

    this.cookieService.set(
      "ReferralId",
      referralId,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delReferralId(options: Web.ICookie): void {
    this.cookieService.delete("ReferralId", options.path, options.domain);
  }

  /**
   * RoleId
   */
  getRoleId(): number {
    return Number(this.cookieService.get("roleID") || this.defaultRoleId);
  }

  setRoleId(roleId: string | number, options: Web.ICookie): void {
    this.delRoleId(options);

    this.cookieService.set(
      "roleID",
      String(roleId),
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delRoleId(options: Web.ICookie): void {
    this.cookieService.delete("roleID", options.path, options.domain);
  }

  /**
   * PayPageId for EProtect Credit Card
   */
  getPayPageId(): string {
    return this.cookieService.get("payPageId") || '';
  }

  setPayPageId(payPageId: string, options: Web.ICookie): void {
    this.delPayPageId(options);

    this.cookieService.set(
      "payPageId",
      payPageId || '',
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delPayPageId(options: Web.ICookie): void {
    this.cookieService.delete("payPageId", options.path, options.domain);
  }

  /**
   * Last UI
   */
  getLastUi(): string {
    return this.cookieService.get("lastUI") || '';
  }

  setLastUI(lastUI: string = this.appUi, options: Web.ICookie): void {
    this.delLastUI(options);

    this.cookieService.set(
      "lastUI",
      lastUI,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delLastUI(options: Web.ICookie): void {
    this.cookieService.delete("lastUI", options.path, options.domain);
  }

  /**
   * Is Remember Me
   */
  getIsRememberMe(): boolean {
    return this.cookieService.get("isRememberMe") == 'true' ? true : false;
  }

  setIsRememberMe(isRememberMe: string, options: Web.ICookie): void {
    this.cookieService.set(
      "isRememberMe",
      isRememberMe,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delIsRememberMe(options: Web.ICookie): void {
    this.cookieService.delete("isRememberMe", options.path, options.domain);
  }

  /**
   * Is Remember Email
   * to avoid conflict with GUEST as email used in other components
   */
  getIsRememberEmail(): string {
    return this.cookieService.get("isRememberEmail") || '';
  }

  setIsRememberEmail(email: string, options: Web.ICookie): void {
    this.cookieService.set(
      "isRememberEmail",
      email,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delIsRememberEmail(options: Web.ICookie): void {
    this.cookieService.delete("isRememberEmail", options.path, options.domain);
  }

  /**
   * Prev Url
   */
  getPrevUrl(): string {
    return this.cookieService.get("prevUrl") || '';
  }

  // , options: Web.ICookie
  setPrevUrl(prevUrl: string): void {
    const options1day: Web.ICookie = this.getOptions('/', 1);
    this.cookieService.set(
      "prevUrl",
      prevUrl,
      options1day.expires,
      options1day.path,
      options1day.domain,
      true
    );
  }

  delPrevUrl(options: Web.ICookie): void {
    this.cookieService.delete("prevUrl", options.path, options.domain);
  }

  /**
   * ticket
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTicket(): any {
    return this.cookieService.get("currentTicket") || {};
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setTicket(data: any, options: Web.ICookie): void {
    this.delTicket(options);

    this.cookieService.set(
      "currentTicket",
      data,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delTicket(options: Web.ICookie): void {
    this.cookieService.delete("currentTicket", options.path, options.domain);
  }

  /**
   * change password is active
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChangePasswordIsActive(): any {
    return this.cookieService.get("changePasswordIsActive") == 'true' ? true : false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setChangePasswordIsActive(data: any, options: Web.ICookie): void {
    this.delChangePasswordIsActive(options);

    this.cookieService.set(
      "changePasswordIsActive",
      data,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.platform === 'ios' ? false : true
    );
  }

  delChangePasswordIsActive(options: Web.ICookie): void {
    this.cookieService.delete("changePasswordIsActive", options.path, options.domain);
  }

  /**
   * User State
   */
  getState(): string {
    return this.cookieService.get("state") || '';
  }

  setState(fname: string, options: Web.ICookie): void {
    this.delState(options);
    this.cookieService.set(
      "state",
      fname,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      true
    );
  }

  delState(options: Web.ICookie): void {
    this.cookieService.delete("state", options.path, options.domain);
  }

  /**
   * User Country
   */
  getCountry(): string {
    return this.cookieService.get("country") || '';
  }

  setCountry(fname: string, options: Web.ICookie): void {
    this.delCountry(options);

    this.cookieService.set(
      "country",
      fname,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      true
    );
  }

  delCountry(options: Web.ICookie): void {
    this.cookieService.delete("country", options.path, options.domain);
  }

}
